import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { EmailTestStatus, type TestingStatus } from '@regigov/contracts';
import { testingApi } from '../api/testing';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { isCerAdminRole } from '../auth/roles';
import { IdentitySwitcher } from '../components/IdentitySwitcher';

const SimStatus = z.object({ simMode: z.literal(true) });

/** True when the API runs with SIM_MODE=true; /sim/status doesn't exist otherwise. */
function useSimMode(signedIn: boolean): boolean {
  const [simMode, setSimMode] = useState(false);
  useEffect(() => {
    if (!signedIn) {
      setSimMode(false);
      return;
    }
    api('/sim/status', SimStatus).then(() => setSimMode(true), () => setSimMode(false));
  }, [signedIn]);
  return simMode;
}

/** True when the API runs with EMAIL_TEST_UI=true and the user may use it; /email-test/status is 404 or 403 otherwise. */
function useEmailTestUi(user: { id: string; workbench: string; roles: readonly string[] } | null): boolean {
  const [enabled, setEnabled] = useState(false);
  const key = user?.workbench === 'cer' ? `${user.id}:${user.roles.join(',')}` : null;
  useEffect(() => {
    if (!key) {
      setEnabled(false);
      return;
    }
    api('/email-test/status', EmailTestStatus).then(() => setEnabled(true), () => setEnabled(false));
  }, [key]);
  return enabled;
}

/** The API's TESTING_MODE status for this user, or null when testing mode is off (/testing/status is 404). */
function useTestingStatus(user: { id: string; roles: readonly string[] } | null): TestingStatus | null {
  const [status, setStatus] = useState<TestingStatus | null>(null);
  const key = user ? `${user.id}:${user.roles.join(',')}` : null;
  useEffect(() => {
    if (!key) {
      setStatus(null);
      return;
    }
    testingApi.status().then(setStatus, () => setStatus(null));
  }, [key]);
  return status;
}

const navByWorkbench = {
  cer: [
    { to: '/cer', label: 'Overview' },
    { to: '/cer/participants', label: 'Participants' },
    { to: '/cer/register', label: 'Register' },
    { to: '/cer/incidents', label: 'Incidents' },
    { to: '/cer/recalls', label: 'Recalls' },
    { to: '/cer/exchange', label: 'Exchange' },
    { to: '/cer/compliance', label: 'Compliance' },
    { to: '/cer/reports', label: 'Reports' },
    { to: '/cer/audit', label: 'Audit trail' },
  ],
  participant: [
    { to: '/participant', label: 'Overview' },
    { to: '/participant/devices', label: 'My devices' },
    { to: '/participant/incidents', label: 'Incidents' },
    { to: '/participant/recalls', label: 'Recalls' },
    { to: '/participant/compliance', label: 'Compliance' },
    { to: '/participant/organisation', label: 'Organisation' },
  ],
  applicant: [
    { to: '/applicant', label: 'Application' },
  ],
} as const;

export function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const simMode = useSimMode(Boolean(user));
  const emailTestUi = useEmailTestUi(user ?? null);
  const testing = useTestingStatus(user ?? null);
  const nav = user
    ? [
        ...navByWorkbench[user.workbench],
        ...(user.workbench === 'cer' && isCerAdminRole(user.roles) ? [{ to: '/cer/users', label: 'Users' }] : []),
        ...(emailTestUi ? [{ to: '/cer/email-test', label: 'Email test' }] : []),
        ...(testing?.admin && user.workbench === 'cer' ? [{ to: '/cer/testing', label: 'Testing' }] : []),
        { to: `/${user.workbench}/help`, label: 'Help' },
      ]
    : [];

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">RegiGov</span>
        <span className="topbar__context">{user ? workbenchLabel(user.workbench) : ''}</span>
        <span className="topbar__spacer" />
        {user && (
          <>
            {testing?.tester ? (
              <IdentitySwitcher key={`${user.id}:${user.roles.join(',')}:${user.participantId ?? ''}`} user={user} />
            ) : (
              <span className="topbar__user">
                {user.displayName} <span className="muted">({user.roles.join(', ')})</span>
              </span>
            )}
            <button
              className="linklike"
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
            >
              Sign out
            </button>
          </>
        )}
      </header>
      {testing && (
        <div className="testing-banner" role="status">
          Testing mode: this is a sandbox for testers, and accounts may change roles and organisations.
        </div>
      )}
      {simMode && (
        <div className="sim-banner" role="status">
          Synthetic data: this register is running in simulation mode and holds generated records.
        </div>
      )}
      <div className="body">
        <nav className="sidenav" aria-label="Workbench">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to.split('/').length === 2}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function workbenchLabel(w: 'cer' | 'participant' | 'applicant'): string {
  return { cer: 'CER workbench', participant: 'Participant workbench', applicant: 'Applicant workbench' }[w];
}
